import React, { useRef } from 'react'
import { Box, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import AdjacentLabels from '../../../Labels/AdjacentLabels'
import getLocaleCurrencyForAmount from '../../../../utils/getLocaleCurrencyForAmount'
import { formatBigFixedTwoDecimals } from '../../../../utils/bigJSHelper'
import AcceptHosted, {
  TransactionResponse,
} from '../../../Account/Billing/AcceptHosted'
import { webhooksApi } from '../../../../api/swagger'
import { EnterPaymentInformationContentProps } from '../types'
import { PaymentModalSteps } from '../../utils/paymentModalSteps'

const paymentSummaryTextStyle = { fontSize: '18px' }

export const EnterPaymentInformationContent: React.FC<
  EnterPaymentInformationContentProps
> = ({
  isLicensing,
  isBankSelected,
  isCardSelected,
  amountDue,
  paymentInformation,
  taxes,
  totalAchDiscount,
  amountDueBigValue,
  total,
  sharedFieldProps,
  bankPaymentInformation,
  validRoutingNumber,
  bankAccountMatch,
  bankAccountNumberMatch,
  confirmBankAccountNumberMatch,
  setErrorMessage,
  backToBillingAddress,
  setStep,
}) => {
  const { t } = useTranslation()
  const formRef = useRef<HTMLFormElement>(null)

  const paymentSummary = (
    <Box
      display="flex"
      justifyContent="space-between"
      flexDirection={{ xs: 'column', sm: 'row' }}
      mt={1}
      mb={3}
      width="100%"
    >
      <Box>
        {!isLicensing ? (
          <AdjacentLabels
            leftLabel={t('PaymentModal.Label.EnrollmentFee', 'Membership Fee')}
            rightLabel={getLocaleCurrencyForAmount(
              isBankSelected
                ? formatBigFixedTwoDecimals(
                    amountDueBigValue.minus(totalAchDiscount)
                  )
                : amountDue.amount,
              amountDue.currencyCode
            )}
            leftStyle={paymentSummaryTextStyle}
            rightStyle={paymentSummaryTextStyle}
          />
        ) : (
          <AdjacentLabels
            leftLabel={t('PaymentModal.Label.EnrollmentFee', 'License Fee')}
            rightLabel={getLocaleCurrencyForAmount(
              (paymentInformation?.payment?.amount?.amount as string) ?? '0.00',
              amountDue.currencyCode
            )}
            leftStyle={paymentSummaryTextStyle}
            rightStyle={paymentSummaryTextStyle}
          />
        )}
      </Box>
      <Box>
        <AdjacentLabels
          leftLabel={t('PaymentModal.Label.Taxes', 'Taxes')}
          rightLabel={getLocaleCurrencyForAmount(taxes, 'USD')}
          leftStyle={paymentSummaryTextStyle}
          rightStyle={paymentSummaryTextStyle}
        />
      </Box>
      <Box>
        {!isLicensing ? (
          <AdjacentLabels
            leftLabel={t('PaymentModal.Label.Total', 'Total')}
            rightLabel={getLocaleCurrencyForAmount(
              formatBigFixedTwoDecimals(
                isBankSelected ? total.minus(totalAchDiscount) : total
              ),
              'USD'
            )}
            leftStyle={paymentSummaryTextStyle}
            rightStyle={paymentSummaryTextStyle}
          />
        ) : (
          <AdjacentLabels
            leftLabel={t('PaymentModal.Label.Total', 'Total')}
            rightLabel={getLocaleCurrencyForAmount(
              (paymentInformation?.payment?.total?.amount as string) ?? '0.00',
              'USD'
            )}
            leftStyle={paymentSummaryTextStyle}
            rightStyle={paymentSummaryTextStyle}
          />
        )}
      </Box>
    </Box>
  )

  return (
    <>
      {isBankSelected && (
        <Box display="flex" flexDirection="column">
          <Box
            mb={3}
            display="flex"
            flexDirection={{ xs: 'column', lg: 'row' }}
          >
            <Box
              width={{ sm: '100%', lg: '50%' }}
              pr={{ sm: 0, lg: 2 }}
              mb={{ sm: 3, lg: 0 }}
            >
              <TextField
                {...sharedFieldProps}
                id="firstName"
                name="firstName"
                variant="filled"
                label={t('PaymentModal.Field.FirstName', 'First Name')}
                value={bankPaymentInformation.firstName}
                fullWidth
              />
            </Box>
            <Box width={{ sm: '100%', lg: '50%' }} pl={{ sm: 0, lg: 2 }}>
              <TextField
                {...sharedFieldProps}
                id="lastName"
                name="lastName"
                variant="filled"
                label={t('PaymentModal.Field.LastName', 'Last Name')}
                value={bankPaymentInformation.lastName}
                fullWidth
              />
            </Box>
          </Box>
          <Box mb={3}>
            <TextField
              {...sharedFieldProps}
              id="routingNumber"
              name="routingNumber"
              variant="filled"
              error={!validRoutingNumber.input}
              helperText={!validRoutingNumber.input && bankAccountNumberMatch}
              label={t(
                'PaymentModal.Field.BankRoutingNumber',
                'Bank Routing Number (9 digits)'
              )}
              value={bankPaymentInformation.routingNumber}
              inputProps={{ maxLength: 9, minLength: 9 }}
              fullWidth
            />
          </Box>
          <Box mb={3}>
            <TextField
              {...sharedFieldProps}
              id="bankAccountNumber"
              name="bankAccountNumber"
              variant="filled"
              error={!bankAccountMatch.input}
              helperText={!bankAccountMatch.input && bankAccountNumberMatch}
              label={t(
                'PaymentModal.Field.BankAccountNumber',
                'Bank Account Number'
              )}
              value={bankPaymentInformation.bankAccountNumber}
              inputProps={{ maxLength: 20 }}
              fullWidth
            />
          </Box>
          <Box mb={3}>
            <TextField
              {...sharedFieldProps}
              id="confirmBankAccountNumber"
              name="confirmBankAccountNumber"
              variant="filled"
              error={!bankAccountMatch.input}
              helperText={
                !bankAccountMatch.input && confirmBankAccountNumberMatch
              }
              label={t(
                'PaymentModal.Field.ConfirmBankAccountNumber',
                'Confirm Bank Account Number'
              )}
              value={bankPaymentInformation.confirmBankAccountNumber}
              inputProps={{ maxLength: 20 }}
              fullWidth
            />
          </Box>
          {paymentSummary}
        </Box>
      )}
      {isCardSelected && (
        <>
          {paymentSummary}
          <AcceptHosted
            formToken={paymentInformation?.ccFormToken ?? ''}
            formRef={formRef}
            onCancel={async () => {
              setErrorMessage('')
              backToBillingAddress()
            }}
            onTransact={async (response: TransactionResponse) => {
              if (!!response && !response.errors) {
                setErrorMessage('')
                try {
                  await webhooksApi.authorizeDotNetEventWebhook({
                    body: {
                      payload: {
                        responseCode: +response.responseCode,
                        merchantReferenceId: response.refId,
                        id: response.transId,
                      },
                    },
                  })
                } catch (error) {
                  console.error('Webhook call failed:', error)
                }
                setStep(PaymentModalSteps.PaymentComplete)
              } else {
                console.error(
                  'A.Net transaction response has errors:',
                  response.errors
                )
              }
            }}
          />
        </>
      )}
    </>
  )
}

export default EnterPaymentInformationContent
